import { useState } from "react";
import ReactPaginate from "react-paginate";
import { useQuery, useQueryClient } from "react-query";
import { useAppModal } from "components/common/AppModalProvider";
import { getAdminServiceList, patchDeactivateService, patchActivateService } from "apis";

const ServiceManageComponent = () => {
    const queryClient = useQueryClient();
    const { showAlert, showConfirm } = useAppModal();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { data, isLoading, isError } = useQuery(
        ["adminServiceList", currentPage, itemsPerPage],
        () => getAdminServiceList(currentPage, itemsPerPage),
        { keepPreviousData: true }
    );

    if (isLoading) return <div className="p-8"><div className="animate-pulse h-96 bg-achromatic-100 rounded-2xl" /></div>;
    if (isError) return <div className="p-8 text-red-500">오류가 발생했습니다.</div>;

    const serviceList = (data as any)?.data?.data?.items || [];
    const totalItems = (data as any)?.data?.data?.total || 0;
    const pageCount = Math.ceil(totalItems / itemsPerPage);

    const handlePageClick = ({ selected }: { selected: number }) => setCurrentPage(selected + 1);

    const handleToggleActive = async (serviceId: string, isActive: boolean) => {
        const action = isActive ? '비활성화' : '활성화';
        if (!(await showConfirm(`서비스를 ${action}하시겠습니까?`, { title: `서비스 ${action}`, confirmText: '확인', variant: isActive ? 'warning' : 'info' }))) return;
        try {
            const response = isActive
                ? await patchDeactivateService(serviceId)
                : await patchActivateService(serviceId);
            if ((response as any).status === 200) {
                showAlert(`${action} 처리되었습니다.`, { variant: 'success' });
                queryClient.invalidateQueries(["adminServiceList"]);
            }
        } catch (error) {
            showAlert("처리 중 오류가 발생했습니다.", { variant: 'error' });
        }
    };

    return (
        <div className="p-8">
            <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-achromatic-900">서비스 관리</h1>
                <p className="text-sm text-achromatic-400 mt-1">전체 {totalItems}건의 등록 서비스</p>
            </div>

            <div className="bg-white rounded-2xl border border-achromatic-100 overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-achromatic-100 bg-achromatic-50">
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">#</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">서비스명</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-left">호스트</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-right">가격</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">카테고리</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">스케줄</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">상태</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">등록일</th>
                            <th className="py-3 px-4 text-[12px] font-semibold text-achromatic-500 uppercase tracking-wider text-center">액션</th>
                        </tr>
                    </thead>
                    <tbody>
                        {serviceList.map((item: any, index: number) => (
                            <tr key={item.id} className="border-b border-achromatic-50 hover:bg-primary-50/30 transition-colors">
                                <td className="py-3 px-4 text-[13px] text-achromatic-500">{index + 1 + (currentPage - 1) * itemsPerPage}</td>
                                <td className="py-3 px-4 text-[13px] font-semibold text-achromatic-900 max-w-[200px] truncate">{item.title}</td>
                                <td className="py-3 px-4 text-[13px] text-achromatic-600">{item.hostName}</td>
                                <td className="py-3 px-4 text-[13px] font-bold text-achromatic-900 text-right">
                                    {Number(item.price).toLocaleString()} <span className="text-[11px] font-medium text-achromatic-400">원</span>
                                </td>
                                <td className="py-3 px-4 text-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-600">
                                        {item.category}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">{item.scheduleCount}건</td>
                                <td className="py-3 px-4 text-center">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${item.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                        {item.isActive ? '활성' : '비활성'}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-[13px] text-achromatic-500 text-center">
                                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : '-'}
                                </td>
                                <td className="py-3 px-4 text-center">
                                    <button
                                        className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${item.isActive
                                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}
                                        onClick={() => handleToggleActive(item.id, item.isActive)}
                                    >
                                        {item.isActive ? '비활성화' : '활성화'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="p-4 border-t border-achromatic-100 flex justify-center">
                    <ReactPaginate
                        previousLabel={"←"} nextLabel={"→"} breakLabel={"..."} pageCount={pageCount}
                        marginPagesDisplayed={1} pageRangeDisplayed={5} onPageChange={handlePageClick}
                        containerClassName={"flex items-center gap-1"}
                        previousClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        nextClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-500 hover:bg-achromatic-100"}
                        pageClassName={"w-8 h-8 flex items-center justify-center rounded-lg text-[13px] text-achromatic-600 hover:bg-achromatic-100"}
                        activeClassName={"!bg-primary-500 !text-white"} activeLinkClassName={"text-white"}
                    />
                </div>
            </div>
        </div>
    );
};

export default ServiceManageComponent;
